import type {
  EmployeeBilletterieOrders,
  EmployeeListRow,
  EmployeesStats,
  EmployeeWithBeneficiaries,
  SelectableBeneficiaryRow,
  TiersAccountState,
} from '@cpce/shared';
import type { TicketingSite } from '@cpce/ticketing-api';
import { useMemo } from 'react';
import { commiteeInfiniteQuery, commiteeQuery } from '@/api/queries.utils';
import { formatEmployeeWithBeneficiaries } from '@/features/beneficiaries/employees/utils/employees.utils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { employeesQueryKeys } from './employees.query-keys';
import type { BeneficiaryRow, EmployeeWithBeneficiariesFormatted } from './employees.types';

// The whole CSE in one response, so the table can sort, filter and search across every salarié
// rather than across the pages it happened to have loaded.
export function useEmployeeList() {
  return commiteeQuery<EmployeeListRow[]>({
    queryKey: employeesQueryKeys.list,
    url: '/commitee/:commiteeId/employees/list',
  });
}

export function useEmployeesStats() {
  return commiteeQuery<EmployeesStats>({
    queryKey: employeesQueryKeys.all,
    url: '/commitee/:commiteeId/employees/stats',
  });
}

// A salarié search takes up to a second on a large CSE: one request per pause, not per keystroke.
const EMPLOYEE_SEARCH_DEBOUNCE_MS = 300;

export function useEmployeesInfinite({ search }: { search?: string } = {}) {
  const params = { search: useDebouncedValue(search, EMPLOYEE_SEARCH_DEBOUNCE_MS) };
  const result = commiteeInfiniteQuery<EmployeeWithBeneficiaries>({
    queryKey: employeesQueryKeys.infinite(params),
    url: '/commitee/:commiteeId/employees',
    params,
  });
  const data = useMemo(
    () => result.data.map(formatEmployeeWithBeneficiaries) as EmployeeWithBeneficiariesFormatted[],
    [result.data]
  );
  return { ...result, data };
}

export function useSites() {
  return commiteeQuery<TicketingSite[]>({
    queryKey: employeesQueryKeys.sites,
    url: '/commitee/:commiteeId/employees/sites',
  });
}

export function useEmployee(id: number, options?: { enabled?: boolean }) {
  return commiteeQuery<EmployeeWithBeneficiaries, EmployeeWithBeneficiariesFormatted>({
    queryKey: employeesQueryKeys.one(id),
    url: `/commitee/:commiteeId/employees/${id}`,
    transform: formatEmployeeWithBeneficiaries,
    enabled: options?.enabled,
  });
}

export function useEmployeeFinancialAccount(id: number) {
  return commiteeQuery<TiersAccountState>({
    queryKey: employeesQueryKeys.financialAccount(id),
    url: `/commitee/:commiteeId/employees/${id}/financial-account`,
  });
}

export function useEmployeeOrders(id: number, options?: { enabled?: boolean }) {
  return commiteeQuery<EmployeeBilletterieOrders>({
    queryKey: employeesQueryKeys.orders(id),
    url: `/commitee/:commiteeId/employees/${id}/orders`,
    enabled: options?.enabled,
  });
}

export function useSelectableBeneficiaries() {
  return commiteeQuery<SelectableBeneficiaryRow[], BeneficiaryRow[]>({
    queryKey: employeesQueryKeys.selectable,
    url: '/commitee/:commiteeId/employees/selectable',
    transform: (rows) =>
      rows.map((row) => ({
        ...row,
        firstName: row.firstName ?? '',
        lastName: row.lastName ?? '',
        birthDate: row.birthDate ? new Date(row.birthDate) : null,
        contractStartDate: row.contractStartDate ? new Date(row.contractStartDate) : null,
      })),
  });
}
